import { Seo } from '../components/academy/Seo';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const Contact = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate sending message
    setTimeout(() => {
      toast.success('Message sent! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
      <Seo siteName="CarLens" title="Contact CarLens | Book a Vehicle Inspection" description="Get in touch with CarLens for independent mobile pre-purchase vehicle inspections in Northeast Florida. Call 321.300.3256 or send us a message." path="/contact" />
      {/* Header */}
      <header className="border-b border-zinc-800 backdrop-blur-sm bg-black/30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/advll02n_ChatGPT%20Image%20Jul%2011%2C%202026%2C%2004_01_30%20PM.png" 
              alt="CarLens" 
              className="h-16 w-auto max-w-[220px] sm:h-24 md:h-36 md:max-w-none cursor-pointer object-contain"
              onClick={() => navigate('/')}
            />
          </div>
          <nav className="flex items-center gap-6">
          </nav>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="inline-block mb-6">
            <div className="px-4 py-2 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-400 text-sm font-medium">
              Get in Touch
            </div>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>
            Contact Us
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Have questions about our inspection services? We're here to help!
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Contact Info Cards */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 text-center">
            <div className="w-16 h-16 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Email Us</h3>
            <a href="mailto:carlensauto@gmail.com" className="text-orange-400 hover:text-orange-300">
              carlensauto@gmail.com
            </a>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 text-center">
            <div className="w-16 h-16 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mx-auto mb-6">
              <Phone className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Call or Text</h3>
            <a href="tel:+13213003256" className="text-orange-400 hover:text-orange-300">
              321.300.3256
            </a>
            <p className="text-zinc-500 text-sm mt-2">Mon-Sat: 8am - 6pm</p>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 text-center">
            <div className="w-16 h-16 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Service Area</h3>
            <p className="text-zinc-300">North Florida</p>
            <p className="text-zinc-300">South Georgia</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 lg:p-12">
          <h2 className="text-3xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Space Grotesk' }}>
            Send Us a Message
          </h2>

          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="text-zinc-300">Your Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  placeholder="John Doe"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-zinc-300">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  placeholder="you@example.com"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone" className="text-zinc-300">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  placeholder="(555) 123-4567"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <Label htmlFor="subject" className="text-zinc-300">Subject *</Label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  placeholder="Inspection inquiry"
                  data-testid="contact-subject-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="message" className="text-zinc-300">Message *</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                placeholder="Tell us how we can help..."
                data-testid="contact-message-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg font-semibold rounded-lg"
              data-testid="submit-contact-btn"
            >
              {loading ? (
                'Sending...'
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </>
              )}
            </Button>

            <p className="text-center text-sm text-zinc-500 mt-4">
              We typically respond within 24 hours
            </p>
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-24">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-zinc-500">
          <p>© 2025 CarLens Inspections. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
